import { type IResponse, encryptedRequest } from "../core/api.client";
export const LIVE_COMMERCE_API_PATHS = {
  SET_REMINDER: `/api/v1/live-commerce/reminder`,
  REMOVE_REMINDER: `/api/v1/live-commerce/remove-reminder`,
  GET_USER_REMINDERS: `/api/v1/live-commerce/reminders/user`,
  FOLLOW_STATUS: `/api/v1/live-commerce/follow/status`,
  FOLLOW: `/api/v1/live-commerce/follow`,
  GET_USER_FOLLOWING: `/api/v1/live-commerce/followings`,
} as const;

export interface SetReminderRequest {
  mobileNo: string;
  influencerId: string;
  videoId: string;
}

export interface LiveCommerceReminder {
  id: string;
  influencerId: string;
  videoId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetReminderResponse {
  reminder: LiveCommerceReminder;
  isNew: boolean;
}

export interface RemoveReminderRequest {
  mobileNo: string;
  videoId: string;
  influencerId: string;
}

export interface RemoveReminderResponse {
  deleted: boolean;
}

export interface GetUserRemindersRequest {
  mobileNo: string;
  influencerId?: string;
}

export interface GetUserRemindersResponse {
  count: number;
  reminders: LiveCommerceReminder[];
}

export interface GetFollowStatusRequest {
  mobileNo: string;
  influencerId: string;
}

/** Backend may expose one of these; UI should treat unknown shapes as not following */
export interface GetFollowStatusResponse {
  isFollowing?: boolean;
  following?: boolean;
  follow?: boolean;
}

export interface GetUserFollowingRequest {
  mobileNo: string;
}

export interface UserFollowingCreator {
  creatorId: string;
  influencerImage: string;
  influencerName: string;
}

export interface GetUserFollowingResponse {
  creators: UserFollowingCreator[];
}

export interface FollowInfluencerRequest {
  mobileNo: string;
  influencer: { id: string };
}

/** Same POST toggles follow/unfollow; payload reflects resulting state when the API sends it */
export interface FollowInfluencerResponse {
  follow?: boolean;
}

export const setReminder = async (
  payload: SetReminderRequest
): Promise<IResponse<SetReminderResponse>> => {
  try {
    const response = await encryptedRequest<SetReminderResponse, SetReminderRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.SET_REMINDER,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to set reminder");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while setting reminder");
  }
};

export const removeReminder = async (
  payload: RemoveReminderRequest
): Promise<IResponse<RemoveReminderResponse>> => {
  try {
    const response = await encryptedRequest<RemoveReminderResponse, RemoveReminderRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.REMOVE_REMINDER,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to remove reminder");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while removing reminder");
  }
};

export const getFollowStatus = async (
  payload: GetFollowStatusRequest
): Promise<IResponse<GetFollowStatusResponse>> => {
  try {
    const response = await encryptedRequest<GetFollowStatusResponse, GetFollowStatusRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.FOLLOW_STATUS,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to get follow status");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while getting follow status");
  }
};

export const getUserFollowing = async (
  payload: GetUserFollowingRequest
): Promise<IResponse<GetUserFollowingResponse>> => {
  try {
    const response = await encryptedRequest<GetUserFollowingResponse, GetUserFollowingRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.GET_USER_FOLLOWING,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to get user following");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while getting user following");
  }
};

export const followInfluencer = async (
  payload: FollowInfluencerRequest
): Promise<IResponse<FollowInfluencerResponse>> => {
  try {
    const response = await encryptedRequest<FollowInfluencerResponse, FollowInfluencerRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.FOLLOW,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to follow influencer");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while following influencer");
  }
};

export const getUserReminders = async (
  payload: GetUserRemindersRequest
): Promise<IResponse<GetUserRemindersResponse>> => {
  try {
    const response = await encryptedRequest<GetUserRemindersResponse, GetUserRemindersRequest>({
      method: "POST",
      url: LIVE_COMMERCE_API_PATHS.GET_USER_REMINDERS,
      data: payload,
    });

    if (response.statusCode !== 90 || response.status !== "success") {
      throw new Error(response.message || "Failed to get user reminders");
    }

    return response;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("An unexpected error occurred while getting user reminders");
  }
};

export const liveCommerceService = {
  setReminder,
  removeReminder,
  getUserReminders,
  getFollowStatus,
  followInfluencer,
  getUserFollowing,
};
